import { redirect } from "@tanstack/react-router";
import { logError } from "@/lib/structured-log";
import type { OnboardingStatusResponse } from "@/lib/api/generated";

/**
 * Extended route context that includes workspace data from parent beforeLoad
 *
 * NOTE: In TanStack Router, context only includes data from beforeLoad, not loader.
 * The workspaceSlug is provided via beforeLoad for child routes to access.
 */
export interface RouteContextWithWorkspace {
  onboardingStatus?: OnboardingStatusResponse;
  /** Workspace slug from URL (provided by parent workspace layout route's beforeLoad) */
  workspaceSlug?: string;
  /** @deprecated Use parentMatchPromise to get workspaceId from parent loader instead */
  workspaceId?: string;
}

/**
 * Parent match result with loader data
 */
interface ParentMatchWithLoaderData {
  loaderData?: {
    workspaceId?: string;
  };
  parentMatch?: ParentMatchWithLoaderData | null;
}

/**
 * Get workspace ID from parent loader data
 *
 * IMPORTANT: This function should be used by all child routes under /workspace/$workspaceSlug
 * The parent workspace layout route's loader provides workspaceId after matching URL slug.
 *
 * In TanStack Router, context only includes beforeLoad data. To access parent loader data,
 * we use parentMatchPromise which resolves when the parent loader completes.
 *
 * @param context - Route context from parent beforeLoad
 * @param routeName - Name of the route (for error logging)
 * @param parentMatchPromise - Optional promise for parent loader data
 * @returns workspace_id (guaranteed to be non-null)
 * @throws redirect if workspace_id is missing (defensive check)
 */
export async function getWorkspaceIdFromParentLoader(
  context: RouteContextWithWorkspace,
  routeName: string,
  parentMatchPromise?: Promise<ParentMatchWithLoaderData>,
): Promise<string> {
  if (parentMatchPromise) {
    let currentMatch: ParentMatchWithLoaderData | null | undefined = await parentMatchPromise;

    while (currentMatch) {
      const workspaceId = currentMatch.loaderData?.workspaceId;
      if (workspaceId) {
        return workspaceId;
      }
      currentMatch = currentMatch.parentMatch;
    }
  }

  // Fallback: try context.workspaceId (backward compatibility)
  if (context.workspaceId) {
    return context.workspaceId;
  }

  // Last resort: try onboardingStatus.workspace_id
  const workspaceId = context.onboardingStatus?.workspace_id;
  if (workspaceId) {
    return workspaceId;
  }

  // This should NEVER happen if parent loader is working correctly
  logError(`${routeName}.loader.missing_workspace_id`, {
    message: "Failed to get workspaceId from parent loader or context",
    has_context: !!context,
    has_parent_promise: !!parentMatchPromise,
    has_status: !!context.onboardingStatus,
    onboarding_state: context.onboardingStatus?.onboarding_state,
  });

  // Defensive redirect (user should not see this in normal flow)
  throw redirect({ to: "/onboarding/profile" });
}

/**
 * Get workspace ID with fallback (simplified version for backward compatibility)
 *
 * This function tries multiple sources in order:
 * 1. parentMatchPromise (parent loader data) - preferred for new routes
 * 2. context.workspaceId (backward compatibility)
 * 3. context.onboardingStatus.workspace_id (legacy fallback)
 *
 * @param context - Route context from parent beforeLoad
 * @param routeName - Name of the route (for error logging)
 * @param parentMatchPromise - Optional promise for parent loader data
 * @returns workspace_id (guaranteed to be non-null)
 * @throws redirect if workspace_id is missing
 */
export async function getWorkspaceIdWithFallback(
  context: RouteContextWithWorkspace,
  routeName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parentMatchPromise?: Promise<any>,
): Promise<string> {
  if (parentMatchPromise) {
    let currentMatch: ParentMatchWithLoaderData | null | undefined = await parentMatchPromise;

    while (currentMatch) {
      const workspaceId = currentMatch.loaderData?.workspaceId;
      if (workspaceId) {
        return workspaceId;
      }
      currentMatch = currentMatch.parentMatch as ParentMatchWithLoaderData | null | undefined;
    }
  }

  // Fallback: try context.workspaceId (backward compatibility)
  if (context.workspaceId) {
    return context.workspaceId;
  }

  // Last resort: try onboardingStatus.workspace_id
  const workspaceId = context.onboardingStatus?.workspace_id;
  if (workspaceId) {
    return workspaceId;
  }

  // This should NEVER happen if parent loader is working correctly
  logError(`${routeName}.loader.missing_workspace_id`, {
    message: "Failed to get workspaceId from parent loader or context",
    has_context: !!context,
    has_parent_promise: !!parentMatchPromise,
    has_status: !!context.onboardingStatus,
    onboarding_state: context.onboardingStatus?.onboarding_state,
  });

  throw redirect({ to: "/onboarding/profile" });
}

/**
 * @deprecated Use getWorkspaceIdWithFallback with parentMatchPromise instead.
 */
export function getWorkspaceIdSafely(
  context: RouteContextWithWorkspace,
  routeName: string,
): string {
  // Try context.workspaceId first
  if (context.workspaceId) {
    return context.workspaceId;
  }

  // Fallback to onboardingStatus.workspace_id
  const workspaceId = context.onboardingStatus?.workspace_id;

  if (!workspaceId) {
    logError(`${routeName}.loader.missing_workspace_id`, {
      message: "Parent loader should have provided workspace_id in context",
      has_context: !!context,
      has_status: !!context.onboardingStatus,
      onboarding_state: context.onboardingStatus?.onboarding_state,
    });

    throw redirect({ to: "/onboarding/profile" });
  }

  return workspaceId;
}
